"use client";

import type { ReactNode } from "react";

import { dynamicChart } from "@/components/charts/dynamicChart";
import type { LegendEntry } from "@/components/charts/HorizontalCategoricalBar";
import {
  ProportionBar,
  type ProportionSegment,
} from "@/components/charts/ProportionBar";
import { getPalette, type PaletteToken } from "@/lib/palette";
import { usePrefersDark } from "@/lib/usePrefersDark";

import type { FundingRow } from "@/app/climate/_components/funding-types";

const HorizontalCategoricalBar = dynamicChart(
  () =>
    import("@/components/charts/HorizontalCategoricalBar").then(
      (m) => m.HorizontalCategoricalBar,
    ),
  420,
);

type ClassKey = "city" | "outside" | "loan";

const CLASS_LABELS: Record<ClassKey, string> = {
  city: "City of Calgary",
  outside: "Outside-government grants",
  loan: "Federal loan (must be repaid)",
};

const CLASS_TOKENS: Record<ClassKey, PaletteToken> = {
  city: "signal",
  outside: "teal",
  loan: "ochre",
};

const CLASS_DESCRIPTIONS: Record<ClassKey, string> = {
  city:
    "Calgary city budget — recurring base operating, one-time operating, and previously approved capital. Includes the Pay-As-You-Go (city reserve) share of the Centralized Climate Fund.",
  outside:
    "Federal and provincial grants. Includes the federal Zero Emission Transit Fund grant for the EV bus program, other secured grants (active transportation, natural areas, EV charging, energy efficiency, composting), and the MSI (provincial) + CCBF (federal) shares of the Centralized Climate Fund.",
  loan:
    "Canada Infrastructure Bank loan for the EV bus program. Must be repaid, but the financing terms are federal — not local property-tax money.",
};

function classify(row: FundingRow): ClassKey {
  if (row.kind === "federal_loan") return "loan";
  if (row.kind === "federal_grant" || row.kind === "external_grant") {
    return "outside";
  }
  if (row.kind === "mixed") {
    // CCF envelope: MSI (provincial) and CCBF (federal) are outside-government
    // money; PAYG is the only city-reserve share. Per C2022-1051 Att 9 p.210.
    return row.mix_source === "payg" ? "city" : "outside";
  }
  return "city";
}

const KIND_TOKENS: Record<FundingRow["kind"], PaletteToken> = {
  federal_grant: "teal",
  external_grant: "cyan",
  mixed: "violet",
  federal_loan: "ochre",
  city: "signal",
};

const KIND_LABELS: Record<FundingRow["kind"], string> = {
  federal_grant: "Federal grant",
  external_grant: "Provincial + federal grants",
  // CCF rows share this kind because they all come from one fund pool, but
  // each row carries a specific mix_source (MSI / CCBF / PAYG) — the legend
  // names the pool, the bucket label names the source.
  mixed: "Centralized Climate Fund (sliced by funding source)",
  federal_loan: "Federal loan (must be repaid)",
  city: "City of Calgary budget",
};

type Props = {
  data: FundingRow[];
  /** Server-rendered prose that sits between the proportion bar and the detail chart. */
  proportionsCaption: ReactNode;
};

/**
 * Single client boundary for both /climate/funding figures (the proportion
 * roll-up and the per-line bar). Collapsing the two charts into one boundary
 * keeps Recharts in a single chunk graph for this route — see sc-26719.
 */
export function ClimateFundingFigures({
  data,
  proportionsCaption,
}: Props) {
  const p = getPalette(usePrefersDark());
  // Proportion segments
  const totals: Record<ClassKey, number> = { city: 0, outside: 0, loan: 0 };
  for (const r of data) totals[classify(r)] += r.amount_mcad;
  const fundingTotalMcad = data.reduce((s, r) => s + r.amount_mcad, 0);
  const order: ClassKey[] = ["city", "outside", "loan"];
  const segments: ProportionSegment<{ classKey: ClassKey }>[] = order
    .filter((k) => totals[k] > 0)
    .map((k) => ({
      key: k,
      label: CLASS_LABELS[k],
      value: totals[k],
      color: p[CLASS_TOKENS[k]],
      meta: { classKey: k },
    }));

  // Categorical bar rows + legend
  const max = Math.max(...data.map((d) => d.amount_mcad));
  const xMax = Math.ceil((max + 30) / 50) * 50;
  const rows = data.map((r) => ({
    label: r.bucket,
    value: r.amount_mcad,
    color: p[KIND_TOKENS[r.kind]],
  }));
  const presentKinds = Array.from(new Set(data.map((r) => r.kind)));
  const legend: LegendEntry[] = presentKinds.map((k) => ({
    label: KIND_LABELS[k],
    color: p[KIND_TOKENS[k]],
  }));

  return (
    <>
      <div id="recurring-city" className="mt-6 scroll-mt-28">
        <p className="mb-2 cs-mono text-xs uppercase tracking-wide text-ink-3">
          $
          {fundingTotalMcad.toLocaleString("en-CA", {
            maximumFractionDigits: 1,
          })}
          M total · who is paying
        </p>
        <ProportionBar
          segments={segments}
          height={64}
          valueFormatter={(v) => `$${v.toFixed(1)}M`}
          tooltipDescription={(s) =>
            CLASS_DESCRIPTIONS[(s.meta?.classKey ?? "city") as ClassKey]
          }
        />
        {proportionsCaption}
      </div>
      <div className="mt-8">
        <p className="mb-2 cs-mono text-xs uppercase tracking-wide text-ink-3">
          Detail · individual funding lines
        </p>
        <HorizontalCategoricalBar
          data={rows}
          height={420}
          valueDomain={[0, xMax]}
          xTickFormatter={(v) => `$${v}M`}
          valueLabelFormatter={(v) =>
            `$${v.toLocaleString("en-CA", { maximumFractionDigits: 1 })}M`
          }
          tooltipLabel="Amount"
          tooltipValueFormatter={(v) => `$${v.toFixed(2)}M`}
          categoryWidth={320}
          legend={legend}
        />
      </div>
    </>
  );
}
